import { ApsHub, ApsProject, ApsProjectDataRequest, CreateResourceUserRequest, Permissions, ResourceType } from "@api/apiClient";
import { useGetProjects, useHubs } from "@api/hubs";
import { useProject, useUpdateApsProjectId } from "@api/projects";
import { useCreateResourceUser } from "@api/resourceUsers";
import useUserPermissionStore from "@hooks/useUserPermissionStore";
import i18n from "@i18n/i18n";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { Autocomplete, Box, Button, CircularProgress, Grid, Snackbar, Tooltip, Typography } from "@mui/material";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import { observer } from "mobx-react";
import React, { ChangeEvent, useEffect, useState } from "react";
import ApsUsersGrid from "./ApsUsersGrid";
import ProjectUsersGrid from "./ProjectUsersGrid";

interface AddProjectUserProps {
  projectId: string;
  projectName: string;
  apsHubId: string;
  apsProjectId: string;
  handleRefreshClick: () => void;
}

const AddProjectUser = observer((props: AddProjectUserProps) => {
  const [activeTab, setActiveTab] = useState("2");
  const [openToast, setOpenToast] = useState(false);

  // For tab 1
  const [isUserAdding, setIsUserAdding] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [actionMessage, setActionMessage] = useState("");

  //For Tab 2
  const [selectedApsHub, setSelectedApsHub] = useState<ApsHub | null>(null);
  const [selectedApsProject, setSelectedApsProjects] = useState<ApsProject | null>(null);
  const [userSearchText, setUserSearchText] = useState("");
  const [userSearchInput, setUserSearchInput] = useState("");
  const [assignApsProjectData, setAssignApsProjectData] = useState(false);
  const upStore = useUserPermissionStore();

  const project = useProject(props.projectId);

  useEffect(() => {
    upStore.setResourcePermissions(project.data?.permissions);
  }, [project.data]);

  const hubs = useHubs();
  useEffect(() => {
    const apsHub = hubs.data?.find((x) => x.id === props.apsHubId) || null;
    setSelectedApsHub(apsHub);
  }, [hubs.data]);

  // // Get projects of selected hub
  const apsProjects = useGetProjects(selectedApsHub?.id as any, { enabled: !!selectedApsHub });

  useEffect(() => {
    const apsProject = apsProjects.data?.find((x) => x.id === props.apsProjectId);
    setSelectedApsProjects(apsProject as any);
  }, [apsProjects.data]);

  const createUser = useCreateResourceUser({
    onSuccess: async () => {
      setActionMessage(i18n.t("dashboard-add-user.user-tab.success-message"));
      setOpenToast(true);
    },
  });
  const assignApsProjectId = useUpdateApsProjectId();

  useEffect(() => {
    if (selectedApsHub) {
      apsProjects.refetch();
    }
  }, [selectedApsHub]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const handleBlur = () => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailInput && !regex.test(emailInput)) {
      setEmailError(i18n.t("dashboard-add-user.user-tab.input-validation-error-text"));
    } else {
      setEmailError("");
    }
  };

  const handleAdduser = async () => {
    if (!emailInput || emailError) {
      setEmailError(i18n.t("dashboard-add-user.user-tab.input-validation-error-text"));
      return;
    } else {
      setEmailError("");
    }

    setIsUserAdding(true);
    try {
      const tmpRequest: any = new CreateResourceUserRequest({
        resourceId: props.projectId,
        resourceType: ResourceType.Project,
        email: emailInput,
        apsUserId: "",
        apsRoles: [],
      });
      await createUser.mutateAsync(tmpRequest);

      setEmailInput("");
    } finally {
      setIsUserAdding(false); // Set loading state back to false
    }
  };

  // Tab 2 Methods & Events
  const handleAccountChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const apsHub = hubs.data?.find((x) => x.id === e.target.value) || null;
    setSelectedApsHub(apsHub);
  };

  const handleProjectChange = async (_e: any, newValue: ApsProject | null) => {
    setSelectedApsProjects(newValue);
  };

  const handleAssignApsProject = async () => {
    if (!props.projectId || !selectedApsHub || !selectedApsProject) {
      return;
    }
    setAssignApsProjectData(true);
    try {
      await assignApsProjectId.mutateAsync(
        new ApsProjectDataRequest({
          projectId: props.projectId,
          apsProjectId: selectedApsProject.id,
          apsHubId: selectedApsHub.id,
          apsHubName: selectedApsHub.name || "", // Provide a fallback or default value
          apsProjectName: selectedApsProject.name, // Provide a fallback or default value
        }) as any,
      );
      setActionMessage(i18n.t("dashboard-add-user.user-tab-acc.map-button-success-message"));
      setOpenToast(true);
      props.handleRefreshClick();
    } finally {
      setAssignApsProjectData(false);
    }
  };

  const handleSearchUser = async () => {
    setUserSearchText(userSearchInput);
  };

  useEffect(() => {
    if (userSearchInput.trim() === "") {
      handleSearchUser();
    }
  }, [userSearchInput]);

  return (
    <>
      <Typography variant="h6">
        {i18n.t("dashboard-add-user.project-title")} {props.projectName}
      </Typography>
      <TabContext value={activeTab}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <TabList onChange={handleTabChange}>
            <Tab label={i18n.t("dashboard-add-user.user-tab-text")} value="1" />
            <Tab label={i18n.t("dashboard-add-user.user-acc-tab-text")} value="2" />
          </TabList>
        </Box>
        <TabPanel value="1" sx={{ padding: 0, paddingTop: 2, height: "85%" }}>
          <Grid container spacing={2} sx={{ display: "flex", alignItems: "center" }}>
            <Grid size={9}>
              <TextField
                label={i18n.t("dashboard-add-user.user-tab.input-holder-text")}
                error={!!emailError}
                helperText={emailError}
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onBlur={handleBlur}
                margin="normal"
                variant="outlined"
                fullWidth
                required
                sx={{ "&.MuiFormControl-root": { marginTop: 0 } }}
              />
            </Grid>
            <Grid size={3} sx={{ display: "flex", alignItems: "center" }}>
              <Tooltip title={i18n.t("dashboard-add-user.user-tab.add-button-tooltip")}>
                <Button
                  loading={isUserAdding}
                  variant="contained"
                  onClick={handleAdduser}
                  disabled={!upStore.hasPermission(Permissions.App_Project_External_Link_Add_Users_Add_Member)}
                >
                  {i18n.t("dashboard-add-user.user-tab.add-button-text")}
                </Button>
              </Tooltip>
            </Grid>
          </Grid>
          <Box sx={{ width: "100%", paddingTop: "5px", height: "calc(100% - 72px)" }}>
            <ProjectUsersGrid projectId={props.projectId} />
          </Box>
        </TabPanel>
        <TabPanel value="2" sx={{ padding: 0, paddingTop: 2, height: "80%" }}>
          <Grid container spacing={2} sx={{ width: "100%" }}>
            <Grid size={8}>
              <Autocomplete
                title={i18n.t("dashboard-add-user.user-tab-acc.acc-account")}
                options={hubs.data!}
                value={selectedApsHub}
                getOptionLabel={(option: any) => option.name}
                onChange={(_e: any, newValue: any) => {
                  handleAccountChange({ target: { name: "accountId", value: newValue.id } } as any);
                }}
                isOptionEqualToValue={(option: any, value: any) => option.id === value?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="accountId"
                    label={i18n.t("dashboard-add-user.user-tab-acc.acc-account")}
                    placeholder={i18n.t("dashboard-add-user.user-tab-acc.acc-account")}
                    required
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {hubs.isFetching ? <CircularProgress color="inherit" size={20} disableShrink /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={4}></Grid>
            <Grid size={8}>
              <Autocomplete
                title={i18n.t("dashboard-add-user.user-tab-acc.acc-project")}
                options={apsProjects.data!}
                value={selectedApsProject}
                getOptionLabel={(option: any) => option.name}
                onChange={(_e: any, newValue: ApsProject | null) => {
                  handleProjectChange(_e, newValue);
                }}
                isOptionEqualToValue={(option: any, value: any) => option.id === value?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="projectId"
                    label={i18n.t("dashboard-add-user.user-tab-acc.acc-project")}
                    placeholder={i18n.t("dashboard-add-user.user-tab-acc.acc-project")}
                    required
                    disabled={!selectedApsHub}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {apsProjects.isFetching ? <CircularProgress color="inherit" size={20} disableShrink /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={4} sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center" }} gap={2}>
                <Tooltip title={i18n.t("dashboard-add-user.user-tab-acc.map-button-tooltip")}>
                  <Button
                    loading={assignApsProjectData}
                    variant="contained"
                    onClick={handleAssignApsProject}
                    disabled={!selectedApsProject || !upStore.hasPermission(Permissions.App_Project_External_Link_Add_Users_Map_Aps_Project)}
                  >
                    {i18n.t("dashboard-add-user.user-tab-acc.map-button-text")}
                  </Button>
                </Tooltip>
                <Typography variant="caption"> {!props.apsHubId ? i18n.t("dashboard-add-user.user-tab-acc.acc-project-not-mapped") : ""} </Typography>
              </Box>
            </Grid>

            <Grid size={8}>
              <TextField
                label={i18n.t("dashboard-add-user.user-tab-acc.input-search-text")}
                value={userSearchInput}
                onChange={(e) => setUserSearchInput(e.target.value)}
                margin="normal"
                variant="outlined"
                fullWidth
              />
            </Grid>
            <Grid size={4} sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip title={i18n.t("dashboard-add-user.user-tab-acc.input-search-text")}>
                  <Button variant="contained" onClick={handleSearchUser} disabled={!selectedApsProject}>
                    {i18n.t("dashboard-add-user.user-tab-acc.button-search")}
                  </Button>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ width: "100%", paddingTop: "5px", height: "calc(100% - 180px)" }}>
            <ApsUsersGrid
              apsProjectId={selectedApsProject?.id as any}
              searchText={userSearchText}
              projectId={props.projectId}
              isApsProjectMapped={!!props.apsHubId}
              isApsProjectMatched={props.apsHubId === selectedApsHub?.id && props.apsProjectId === selectedApsProject?.id}
            />
          </Box>
        </TabPanel>
      </TabContext>
      <Snackbar open={openToast} autoHideDuration={2000} anchorOrigin={{ vertical: "top", horizontal: "right" }} message={actionMessage} onClose={() => setOpenToast(false)} />
    </>
  );
});

export default AddProjectUser;
